
export const DEFAULT_BILLABLE_DAYS_PER_MONTH = 18;
export const DEFAULT_INTERMEDIAIRE_MARGIN_PCT = 0.05;
export const ALT_INTERMEDIAIRE_MARGIN_PCT = 0.045;
export const SHORT_MONTH_LABELS = [
  "Jan",
  "Fév",
  "Mar",
  "Avr",
  "Mai",
  "Juin",
  "Juil",
  "Aoû",
  "Sep",
  "Oct",
  "Nov",
  "Déc",
];

const isMissingValue = (value) => value === "" || value === null || value === undefined;

export const normalizeLocaleNumberString = (input) => {
  if (input === null || input === undefined) {
    return "";
  }

  return String(input)
    .trim()
    .replace(/\s+/g, "")
    .replace(/,/g, ".");
};

export const parseLocaleNumber = (input) => {
  if (isMissingValue(input)) {
    return null;
  }

  if (typeof input === "number") {
    return Number.isFinite(input) ? input : null;
  }

  const normalizedValue = normalizeLocaleNumberString(input);
  if (!normalizedValue) {
    return null;
  }

  const numericValue = Number.parseFloat(normalizedValue);
  return Number.isFinite(numericValue) ? numericValue : null;
};

export const normalizePctInputToRate = (input, fallback = null) => {
  const numericValue = parseLocaleNumber(input);

  if (numericValue === null) {
    return fallback;
  }

  if (Math.abs(numericValue) > 1) {
    return numericValue / 100;
  }

  return numericValue;
};

export const getIntermediateCommissionRate = (mission, defaults = {}) => {
  if (mission?.intermediaire_margin_mode === "AMOUNT") {
    const amount = parseLocaleNumber(mission?.intermediaire_margin_amount);
    const tarif = parseLocaleNumber(mission?.tarif_client);
    if (amount !== null && tarif !== null && tarif > 0) {
      return amount / tarif;
    }
  }

  const explicitPct = normalizePctInputToRate(
    mission?.intermediaire_margin_pct ??
      mission?.intermediary_margin_pct ??
      mission?.intermediary_pct,
  );

  if (explicitPct !== null) {
    return Math.max(0, explicitPct);
  }

  const fallbackPct = normalizePctInputToRate(
    defaults.defaultIntermediairePct ??
      defaults.intermediaire_margin_pct ??
      defaults.intermediary_margin_pct ??
      defaults.intermediary_pct,
  );

  if (fallbackPct === null) {
    return DEFAULT_INTERMEDIAIRE_MARGIN_PCT;
  }

  if (defaults.treatDefaultZeroAsMissing && fallbackPct === 0) {
    return DEFAULT_INTERMEDIAIRE_MARGIN_PCT;
  }

  return Math.max(0, fallbackPct);
};

export const resolveIntermediairePct = (mission, defaults = {}) =>
  getIntermediateCommissionRate(mission, defaults);



export const formatPct = (pct) => {
  const ratioValue = normalizePctInputToRate(pct);

  if (ratioValue === null) {
    return "—";
  }

  const percentValue = Math.round(((ratioValue * 100) + Number.EPSILON) * 10) / 10;

  return `${new Intl.NumberFormat("fr-FR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  }).format(percentValue)} %`;
};

/**
 * Frontend utility to add YTD (year-to-date) running totals to monthly series data.
 * Used for visualization of forecast data from backend API.
 * @param {Array} monthlySeries - Array of monthly data objects
 * @returns {Array} Same data with added 'ytd' field containing running total
 */
export const computeYtdFromMonthlySeries = (monthlySeries = []) => {
  if (!Array.isArray(monthlySeries)) {
    return [];
  }

  const candidateKeys = ["value", "ca", "caNet", "net"];
  const fallbackValueKey = candidateKeys.find((candidateKey) =>
    monthlySeries.some((entry) => Number.isFinite(parseLocaleNumber(entry?.[candidateKey]))),
  ) || "value";

  let runningTotal = 0;

  return monthlySeries.map((entry) => {
    const entryValue = parseLocaleNumber(entry?.[fallbackValueKey]) ?? 0;
    runningTotal += entryValue;

    return {
      ...entry,
      ytd: runningTotal,
    };
  });
};
